import { PoolDispatch, PoolWave } from './api';

import { formatMarkedAt } from '@/routes/Agency/DashboardV2/util/dateFormat';

/**
 * Where one job's pool ladder stands, in a few words. `ladder_step` is the
 * furthest step reached (pending -> pool -> widened -> alerted, or skipped
 * when the job has no location); `skipped_reason: 'no_pool'` can sit beside
 * any later step, because an empty pool is stamped as "exhausted" and the
 * ladder still widens on time.
 *
 * `applies: false` on a pending ladder means the ladder will never pick this
 * job up (a long-term assignment, posted before the client opted in, already
 * started, or the master switch is off), so it must not read "Waiting".
 */
export const poolStatusText = (d: PoolDispatch): string => {
  if (!d.auto_invite_enabled && d.ladder_step === 'pending') {
    return 'Not on for this client';
  }
  const noPool = d.skipped_reason === 'no_pool';
  switch (d.ladder_step) {
    case 'skipped':
      return d.skipped_reason === 'no_pool'
        ? 'Skipped: no pool'
        : 'Skipped: job has no location';
    case 'alerted':
      return 'Ops alerted at 2 hours';
    case 'widened':
      return noPool
        ? 'Skipped: no pool · Opened to the marketplace'
        : 'Opened to the marketplace';
    case 'pool':
      return noPool ? 'Skipped: no pool' : 'Inviting the pool';
    default:
      return d.applies ? 'Waiting for the first wave' : 'Not auto-invited';
  }
};

/** "Paused Oct 20, 5:30 AM", or null when running. */
export const pausedText = (d: PoolDispatch, tz?: string | null) => {
  if (!d.paused) return null;
  const at = formatMarkedAt(d.paused_at, tz);
  return at ? `Paused ${at}` : 'Paused';
};

/** Why a wave went out, from its trigger (`posted`, `tier-<id>`, ...). */
export const waveTriggerText = (trigger?: string | null): string => {
  if (!trigger) return '';
  if (trigger === 'posted') return 'Job posted';
  if (trigger.startsWith('tier-')) return 'Surge step';
  if (trigger.startsWith('reopen-')) return 'A spot opened';
  if (trigger.startsWith('resume-')) return 'Resumed';
  if (trigger.startsWith('wave-')) return 'Next wave';
  return '';
};

// The words graphy uses for each exclusion (PoolDispatch::Eligibility
// REASON_LABELS). A wave stores only reason keys, and the eligibility read
// labels only the members left out NOW, so a past wave's reasons need these.
// The server's own `reason_label` still wins where it is known.
export const REASON_LABELS: Record<string, string> = {
  already_hired: 'already hired',
  removed: 'removed from this shift',
  dropped: 'dropped this shift',
  declined: 'declined',
  already_invited: 'already invited',
  already_tried: 'tried, could not be reached',
  suspended: 'suspended',
  not_ready: 'not ready',
  missing_skill: 'missing the skill',
  tax_type: 'wrong tax type',
  background_check: 'no background check',
  blocked: 'blocked by the client',
  quality_gate: 'below the quality gate',
  missing_badge: 'missing a required badge',
  ssn_unverified: 'SSN not verified',
  no_location: 'no location',
  too_far: 'too far',
  conflict: 'conflict',
  daily_cap: "reached today's invite limit",
  invite_failed: 'invite could not be created',
  message_limit: 'waiting for the hourly message limit',
  invite_not_sent: 'invite could not be sent',
};

/**
 * "12 too far · 2 declined", largest first. `labels` (reason -> reason_label
 * from the eligibility read) wins over REASON_LABELS; an unknown reason falls
 * back to its key with underscores as spaces.
 */
export const excludedSummary = (
  counts: PoolWave['excluded_counts'],
  labels: Record<string, string> = {},
): string =>
  Object.entries(counts ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(
      ([reason, n]) =>
        `${n} ${labels[reason] ?? REASON_LABELS[reason] ?? reason.replace(/_/g, ' ')}`,
    )
    .join(' · ');
